import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './DesignCapabilities.css'



const CAPABILITIES = [
    { code: '01', label: 'CUSTOM GEOMETRY', desc: 'Airframe dimensions, arm angles and structural layout optimised for the specific payload and mission profile.' },
    { code: '02', label: 'AGRICULTURE FRAMES', desc: 'Hex and octo layouts designed for heavy spray payloads — high lift, low vibration, field-serviceable.' },
    { code: '03', label: 'SURVEY CLASS', desc: 'Long-endurance fixed-wing and VTOL configurations built around mapping sensor integration.' },
    { code: '04', label: 'HEAVY LIFT', desc: 'Large-format multi-rotor airframes for enterprise payload delivery and inspection operations.' },
]

/* ─── Static top-view wireframe SVG ─────────────────────────────────────── */
function DroneTopView() {
    return (
        <svg viewBox="0 0 440 440" className="dc-drone-svg"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
                <radialGradient id="dc-glow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#5d179e" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#5d179e" stopOpacity="0" />
                </radialGradient>
            </defs>

            <ellipse cx="220" cy="220" rx="170" ry="170" fill="url(#dc-glow)" />

            {/* Reference circles */}
            <circle cx="220" cy="220" r="180" fill="none"
                stroke="rgba(13,10,24,0.06)" strokeWidth="1" strokeDasharray="5 5" />
            <circle cx="220" cy="220" r="120" fill="none"
                stroke="rgba(13,10,24,0.04)" strokeWidth="1" />

            {/* Arms */}
            {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => (
                <line key={i}
                    x1={220 + sx * 26} y1={220 + sy * 26}
                    x2={220 + sx * 118} y2={220 + sy * 118}
                    stroke="rgba(93,23,158,0.55)" strokeWidth="2.2" />
            ))}

            {/* Motor assemblies */}
            {[[102, 102], [338, 102], [102, 338], [338, 338]].map(([x, y], i) => (
                <g key={i}>
                    <circle cx={x} cy={y} r="36" fill="none"
                        stroke="rgba(93,23,158,0.12)" strokeWidth="1" strokeDasharray="3 3" />
                    <ellipse cx={x} cy={y} rx="33" ry="8" fill="none"
                        stroke="rgba(93,23,158,0.18)" strokeWidth="1" />
                    <ellipse cx={x} cy={y} rx="8" ry="33" fill="none"
                        stroke="rgba(93,23,158,0.12)" strokeWidth="1" />
                    <circle cx={x} cy={y} r="22"
                        fill="white" stroke="rgba(93,23,158,0.3)" strokeWidth="1.2" />
                    <circle cx={x} cy={y} r="8"
                        fill="rgba(93,23,158,0.15)" stroke="rgba(93,23,158,0.5)" strokeWidth="1" />
                    <circle cx={x} cy={y} r="3" fill="#5d179e" opacity="0.5" />
                    <line x1={x - 8} y1={y} x2={x + 8} y2={y}
                        stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
                    <line x1={x} y1={y - 8} x2={x} y2={y + 8}
                        stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
                    <text x={x} y={y + 40} textAnchor="middle"
                        fill="rgba(93,23,158,0.4)" fontSize="9" fontFamily="IBM Plex Mono">
                        M{i + 1}
                    </text>
                </g>
            ))}

            {/* FC board */}
            <rect x="196" y="196" width="48" height="48" rx="4"
                fill="white" stroke="rgba(93,23,158,0.45)" strokeWidth="1.3" />
            <rect x="204" y="204" width="32" height="32" rx="2"
                fill="rgba(93,23,158,0.06)" />
            <text x="220" y="222" textAnchor="middle"
                fill="rgba(93,23,158,0.6)" fontSize="8" fontFamily="IBM Plex Mono">FC</text>
            <text x="220" y="233" textAnchor="middle"
                fill="rgba(93,23,158,0.35)" fontSize="6.5" fontFamily="IBM Plex Mono">MOCK FLY</text>
            <circle cx="220" cy="220" r="4" fill="#5d179e" opacity="0.4" />

            {/* Dimension lines */}
            <line x1="102" y1="400" x2="338" y2="400"
                stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            <line x1="102" y1="394" x2="102" y2="406"
                stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            <line x1="338" y1="394" x2="338" y2="406"
                stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            <text x="220" y="418" textAnchor="middle"
                fill="rgba(93,23,158,0.35)" fontSize="8" fontFamily="IBM Plex Mono">
                WHEELBASE
            </text>

            <line x1="32" y1="102" x2="32" y2="338"
                stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            <line x1="26" y1="102" x2="38" y2="102"
                stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            <line x1="26" y1="338" x2="38" y2="338"
                stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />

            {/* Callout tags */}
            <line x1="338" y1="102" x2="378" y2="68"
                stroke="rgba(93,23,158,0.22)" strokeWidth="0.8" strokeDasharray="3 3" />
            <rect x="378" y="52" width="50" height="18" rx="2"
                fill="white" stroke="rgba(93,23,158,0.3)" strokeWidth="0.8" />
            <text x="403" y="65" textAnchor="middle"
                fill="rgba(93,23,158,0.6)" fontSize="8" fontFamily="IBM Plex Mono">BLDC</text>

            <line x1="102" y1="102" x2="62" y2="68"
                stroke="rgba(93,23,158,0.22)" strokeWidth="0.8" strokeDasharray="3 3" />
            <rect x="12" y="52" width="50" height="18" rx="2"
                fill="white" stroke="rgba(93,23,158,0.3)" strokeWidth="0.8" />
            <text x="37" y="65" textAnchor="middle"
                fill="rgba(93,23,158,0.6)" fontSize="8" fontFamily="IBM Plex Mono">PROP</text>

            <line x1="338" y1="338" x2="378" y2="372"
                stroke="rgba(93,23,158,0.22)" strokeWidth="0.8" strokeDasharray="3 3" />
            <rect x="378" y="370" width="50" height="18" rx="2"
                fill="white" stroke="rgba(93,23,158,0.3)" strokeWidth="0.8" />
            <text x="403" y="383" textAnchor="middle"
                fill="rgba(93,23,158,0.6)" fontSize="8" fontFamily="IBM Plex Mono">FRAME</text>
        </svg>
    )
}

export default function DesignCapabilities() {
    const wrapRef = useRef(null)
    const eyebrowRef = useRef(null)
    const h2Ref = useRef(null)
    const svgRef = useRef(null)

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Header
            gsap.fromTo(eyebrowRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
                    scrollTrigger: { trigger: eyebrowRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(h2Ref.current, { opacity: 0, y: 60 },
                {
                    opacity: 1, y: 0, duration: 1.1, ease: 'expo.out',
                    scrollTrigger: { trigger: h2Ref.current, start: 'top 85%', toggleActions: 'play none none none' }
                })
            // SVG
            gsap.fromTo(svgRef.current, { opacity: 0, x: 40 },
                {
                    opacity: 1, x: 0, duration: 1, ease: 'power3.out',
                    scrollTrigger: { trigger: svgRef.current, start: 'top 85%', toggleActions: 'play none none none' }
                })
            // Capability rows
            gsap.fromTo('.dc-row', { opacity: 0, x: -30 },
                {
                    opacity: 1, x: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
                    scrollTrigger: { trigger: '.dc-row', start: 'top 88%', toggleActions: 'play none none none' }
                })
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} className="dc-wrap">
            <div className="dc-bg-grid" />

            <div className="dc-inner">

                {/* Header */}
                <div className="dc-header">
                    <div ref={eyebrowRef} className="dc-eyebrow" style={{ opacity: 0 }}>
                        <span className="dc-dot" />CAPABILITIES
                    </div>
                    <h2 ref={h2Ref} className="dc-h2" style={{ opacity: 0 }}>
                        What We<br /><em>Design.</em>
                    </h2>
                </div>

                {/* Split */}
                <div className="dc-split">

                    {/* Left — capability list */}
                    <div className="dc-list">
                        {CAPABILITIES.map((c) => (
                            <div key={c.code} className="dc-row">
                                <span className="dc-row-code">{c.code}</span>
                                <div className="dc-row-body">
                                    <span className="dc-row-label">{c.label}</span>
                                    <p className="dc-row-desc">{c.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Right — drone wireframe */}
                    <div ref={svgRef} className="dc-visual" style={{ opacity: 0 }}>
                        <DroneTopView />
                    </div>

                </div>

            </div>
        </section>
    )
}